import React, { FC } from 'react';
import PriceComparison from '../../components-library/menu/PriceComparison';
import Theme from '../../components-library/Theme';
import { CompiledProduct } from '../../providers/MenuProvider';
import ProductItemNutritionInfo from '../../components-library/menu/product/ProductItemNutritionInfo';
import collect from 'collect.js';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useTranslation } from 'react-i18next';
import useProductPreviewPrice from '../../features/offers/hooks/useProductPreviewPrice';

type Props = { product: CompiledProduct };

const NutritionInfoBreakdown: FC<Props> = ({ product }) => {
  return (
    <>
      <NetQuantity product={product} />
      <PriceBreakdown product={product} />
      <ProductItemNutritionInfo.Pfand product={product} className="!text-sm" />
      <CaffeineWarning product={product} />
    </>
  );
};

const CaffeineWarning: FC<Props> = ({ product }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:MenuItemsModal' });
  const shouldPresentCaffeineWarning =
    product?.nutrition_info?.caffeine_mg_per_100ml > 0 ||
    collect(product?.nutrition_info?.allergens_list ?? [])
      .map((item) => (item ?? '').toLowerCase())
      .intersect(['koffein', 'caffeine'])
      .isNotEmpty();

  if (!shouldPresentCaffeineWarning) {
    return null;
  }

  const value = product?.nutrition_info?.caffeine_mg_per_100ml
    ? ` (${product?.nutrition_info?.caffeine_mg_per_100ml} mg / 100 ml)`
    : '';

  return <p className="text-xs text-muted-foreground">{t('caffeine_warning', { value })}</p>;
};

const NetQuantity: FC<Props> = ({ product }) => {
  const netQuantityCopy =
    product?.nutrition_info?.net_quantity &&
    `${product?.nutrition_info?.net_quantity.value} ${product?.nutrition_info?.net_quantity.unit}`;
  const alcoCopy =
    product?.nutrition_info?.alcohol_by_volume_percent && `${product?.nutrition_info?.alcohol_by_volume_percent}% vol`;
  const copy = [netQuantityCopy, alcoCopy].filter(Boolean).join(', ');

  if (!copy) {
    return null;
  }

  return <p className="font-medium">{copy}</p>;
};

const PriceBreakdown: FC<Props> = ({ product }) => {
  const { formatCurrency } = useAppMethods();
  // A single-unit price, unlike the modal's CTA which prices the whole line. So for a Bogo offer
  // this row legitimately stays at full price while the CTA (quantity 2) shows the discount.
  const { actualPrice, originalPrice } = useProductPreviewPrice(product);
  const basePriceValue =
    product?.nutrition_info?.base_price && formatCurrency(product?.nutrition_info?.base_price.value_per_unit);
  const basePriceCopy =
    product?.nutrition_info?.base_price && `${basePriceValue}/${product?.nutrition_info?.base_price.unit}`;
  const shouldPresentAlcoholWarning = Boolean(product?.nutrition_info?.alcohol_by_volume_percent);

  return (
    <div className="flex items-center justify-between">
      <PriceComparison
        actualPrice={actualPrice}
        originalPrice={originalPrice}
        originalPriceStyle={{
          fontSize: Theme.font.size.sm,
        }}
        actualPriceStyle={{
          fontSize: Theme.font.size.sm,
          fontWeight: 500,
        }}
      />
      {shouldPresentAlcoholWarning && (
        <span className="!ml-md mr-auto text-danger px-2 py-[2px] bg-danger/10 rounded text-xs font-medium">18+</span>
      )}
      {!!basePriceCopy && <p className="text-sm">{basePriceCopy}</p>}
    </div>
  );
};

export default NutritionInfoBreakdown;
